// Single source of truth for SEO-relevant public routes.
// Imported by:
//   - script/prerender-ssr.ts (snapshot generator at build time)
//   - server/ssr-content.ts (snapshot loader / route validation)
//   - server/routes.ts (sitemap.xml generation)
//   - server/seo-meta.ts (noIndex flag)
//
// To add a new SEO-indexed page:
//   1. Add an entry below (path + sitemap metadata + optional noIndex)
//   2. Create the page component and register it in client/src/App.tsx
//   3. Add a meta entry in server/seo-meta.ts (title/description)
// The next `npm run build` regenerates SSR snapshots automatically.

import { CASE_STUDY_SLUGS } from "./case-studies";
import { SERVICE_DETAIL_SLUGS } from "./services-detail";

export interface PublicRouteConfig {
  path: string;
  changefreq: "weekly" | "monthly" | "yearly";
  priority: string;
  noIndex?: boolean;
}

// Static, hand-curated routes. Case study detail pages are appended below
// from the hardcoded shared/case-studies.ts data so adding a new study
// only requires editing that one file.
const STATIC_ROUTE_CONFIGS: readonly PublicRouteConfig[] = [
  { path: "/", changefreq: "weekly", priority: "1.0" },
  { path: "/servizi", changefreq: "weekly", priority: "0.9" },
  { path: "/chi-siamo", changefreq: "monthly", priority: "0.8" },
  { path: "/contatti", changefreq: "monthly", priority: "0.8" },
  { path: "/preventivo", changefreq: "monthly", priority: "0.9" },
  // Funnel-confirmation page. Reached only after a successful POST /api/quote;
  // noIndex so the "Richiesta ricevuta" copy never competes in SERPs (the
  // robots tag is overridden to "noindex, follow" in server/seo-meta.ts so
  // links from this page are still crawled).
  { path: "/preventivo/grazie", changefreq: "yearly", priority: "0.1", noIndex: true },
  { path: "/erp-conversazionale", changefreq: "weekly", priority: "0.9" },
  // Landing dedicata Consulenza Google Ads. Vive fuori dal registro
  // SERVICES_DETAIL (è una pagina custom in stile erp-conversazionale,
  // non una pillar generata dal template /servizi/:slug) ma deve
  // comparire in sitemap, prerender e snapshot SSR come gli altri
  // servizi indicizzabili.
  { path: "/servizi/google-ads", changefreq: "monthly", priority: "0.8" },
  { path: "/insights", changefreq: "weekly", priority: "0.7" },
  { path: "/case-study", changefreq: "monthly", priority: "0.8" },
  { path: "/valorizzazione-marchio", changefreq: "monthly", priority: "0.8" },
  // Landing funnel Google Ads (form in pagina). Sono pagine di conversione
  // indicizzabili, con Service + FAQPage JSON-LD e SSR body dedicati (vedi
  // server/seo-meta/route-meta.ts). Le rispettive /grazie/* NON sono in
  // sitemap e sono noindex: essendo dinamiche (/grazie/:funnel) non hanno una
  // entry qui, il robots "noindex, follow" è imposto in route-meta.ts.
  { path: "/valorizzazione-marchio/perizia", changefreq: "monthly", priority: "0.8" },
  { path: "/valorizzazione-marchio/conferimento", changefreq: "monthly", priority: "0.8" },
  { path: "/valorizzazione-marchio/cessione", changefreq: "monthly", priority: "0.8" },
  { path: "/valorizzazione-marchio/royalty", changefreq: "monthly", priority: "0.8" },
  { path: "/audit-google-ads", changefreq: "monthly", priority: "0.8" },
  { path: "/servizi/crm-pmi", changefreq: "monthly", priority: "0.8" },
  { path: "/servizi/portale-clienti", changefreq: "monthly", priority: "0.8" },
  { path: "/servizi/gestionale-su-misura", changefreq: "monthly", priority: "0.8" },
  { path: "/newsletter", changefreq: "weekly", priority: "0.6" },
  // Newsletter result pages are transactional landing screens reached only via
  // double-opt-in email links. They MUST be excluded from search indexing
  // and from the sitemap so token-driven URLs and "thank-you" copy don't
  // compete in SERPs. The `noIndex` flag drives both the robots meta in
  // server/seo-meta.ts and the sitemap filter in server/routes.ts.
  { path: "/newsletter/confirmato", changefreq: "yearly", priority: "0.1", noIndex: true },
  { path: "/newsletter/disiscritto", changefreq: "yearly", priority: "0.1", noIndex: true },
  // Public system status page. Indexable=false (transactional/operational
  // surface, not lead-capture) but still prerendered so the chrome lands
  // instantly and the live data hydrates from /api/status on mount.
  { path: "/status", changefreq: "weekly", priority: "0.4", noIndex: true },
  // Status subscribe result pages — same noIndex policy as the newsletter
  // counterparts: transactional landing screens reached only via double-
  // opt-in email links, must not compete in SERPs.
  { path: "/status/confirmato", changefreq: "yearly", priority: "0.1", noIndex: true },
  { path: "/status/disiscritto", changefreq: "yearly", priority: "0.1", noIndex: true },
  { path: "/privacy", changefreq: "yearly", priority: "0.3", noIndex: true },
  { path: "/cookie-policy", changefreq: "yearly", priority: "0.3", noIndex: true },
  { path: "/termini", changefreq: "yearly", priority: "0.3", noIndex: true },
  { path: "/note-legali", changefreq: "yearly", priority: "0.3", noIndex: true },
] as const;

const CASE_STUDY_ROUTE_CONFIGS: readonly PublicRouteConfig[] =
  CASE_STUDY_SLUGS.map((slug) => ({
    path: `/case-study/${slug}`,
    changefreq: "monthly" as const,
    priority: "0.8",
  }));

// Pillar-page routes per i 6 servizi del footer (/servizi/<slug>).
// Vengono espansi automaticamente da SERVICE_DETAIL_SLUGS, così aggiungere
// un servizio richiede solo l'edit del registro shared/services-detail.
const SERVICE_DETAIL_ROUTE_CONFIGS: readonly PublicRouteConfig[] =
  SERVICE_DETAIL_SLUGS.map((slug) => ({
    path: `/servizi/${slug}`,
    changefreq: "monthly" as const,
    priority: "0.8",
  }));

export const PUBLIC_ROUTE_CONFIGS: readonly PublicRouteConfig[] = [
  ...STATIC_ROUTE_CONFIGS,
  ...CASE_STUDY_ROUTE_CONFIGS,
  ...SERVICE_DETAIL_ROUTE_CONFIGS,
] as const;

export const PUBLIC_ROUTES: readonly string[] = PUBLIC_ROUTE_CONFIGS.map(
  (r) => r.path,
);

export type PublicRoute = (typeof PUBLIC_ROUTE_CONFIGS)[number]["path"];

// Header set by the build-time prerender script (script/prerender-ssr.ts).
// The runtime SEO middleware (server/index.ts) skips meta + body injection
// when this header is present, avoiding self-recursion when the prerender
// hits the live server. URL-only flags would let a shared link bypass SSR;
// the header is internal and unforgeable from a regular browser tab.
export const PRERENDER_HEADER_NAME = "x-abmark-prerender";
export const PRERENDER_HEADER_VALUE = "1";

// URL flag used by the React client to suppress overlay UI (cookie banner,
// floating contact, PWA install prompt) during prerender capture so they
// don't end up baked into the snapshot HTML. Must match exactly "1".
export const PRERENDER_URL_PARAM = "__prerender";
export const PRERENDER_URL_VALUE = "1";

export function isPublicRoute(pathname: string): pathname is PublicRoute {
  return (PUBLIC_ROUTES as readonly string[]).includes(pathname);
}

export function getRouteConfig(
  pathname: string,
): PublicRouteConfig | undefined {
  return PUBLIC_ROUTE_CONFIGS.find((r) => r.path === pathname);
}
